import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Checkbox } from '@mui/material';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { APIContext } from '../../Contexts/APIContext';
import { SessionContext } from '../../Contexts/SessionContext';
import { SessionState } from '../../Contexts/SessionState';
import { TelemetryContext } from '../../Contexts/TelemetryContext';
import ResponsiveTable from '../../Utilities/ResponsiveTable';
import SortableTableHead, { HeadCell } from '../../Utilities/SortableTableHead';
import { SiteNavigationType } from "../../Utilities/ViewModels/SiteNavigationType";
import { GetSiteListViewModel, SiteListViewModel } from '../ViewModels/SiteListViewModel';
import LanduseSelection from './LanduseSelection';
import SiteListTextSearchAndLegend from './SiteListTextSearchAndLegend';
import SiteRow from './SiteRow';
import SiteFilterViewModel from '../../TripRate/ViewModels/SiteFilterViewModel';
import { SurveyFullViewModel } from '../../TripRateNew/ViewModels/SurveyFullViewModel';
import TripRateCalculationFiltersViewModel from '../../TripRate/ViewModels/TripRateCalculationFiltersViewModel';
import { navigateToSite, SiteReturnLabel } from '../SiteNavigation';
import { SxProps, Theme } from '@mui/material/styles';

interface IProps {
    type: SiteNavigationType;
    filter?: SiteFilterViewModel;
    setDeselectionOpen?: (id: string) => void;
    multiDeselect?: boolean;
}

const truncatedColumn = (width: string): SxProps<Theme> => ({
    width,
    minWidth: width,
    maxWidth: width,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const getSiteSelectionColumnSx = (property: string, header: string): SxProps<Theme> | undefined => {
    switch (property) {
        case 'Reference':
            return truncatedColumn('calc(11.5ch + 16px)');
        case 'Description':
            return truncatedColumn('calc(25ch + 16px)');
        case 'HasMap':
            return {
                ...truncatedColumn('calc(3ch + 16px)'),
                textOverflow: 'clip',
            };
        case 'Area':
            return truncatedColumn('calc(25em + 16px)');
        case 'City':
            return truncatedColumn('calc(18ch + 16px)');
        case 'Location':
            return truncatedColumn('calc(21ch + 16px)');
        case 'SurveyType':
            return truncatedColumn('calc(13.5ch + 16px)');
        case 'MostRecentSurveyDate':
            return truncatedColumn('calc(17ch + 16px)');
        case 'DayOfWeekNo':
            return truncatedColumn('calc(13ch + 16px)');
        default:
            return header.trim().toUpperCase() === 'GFA'
                ? truncatedColumn('calc(7ch + 16px)')
                : undefined;
    }
};

// Content width limits for the table's 13px Montserrat font; cell padding is added separately.
const siteListColumnWidths: Record<string, string> = {
    Reference: '6.5rem', // WW-00-W-01
    Description: '14.8rem', // Just over DETACHED & SEMI-DETACHED
    HasMap: '1.9rem', // Map (bold)
    Area: '20.6rem', // CHESTER WEST & CHESTER, NORTH WEST
    City: '10.1rem', // NEWTON-LE-WILLOWS
    Location: '9.6rem', // Neighbourhood Centre
    SurveyType: '7.25rem', // Multi-Modal
    MostRecentSurveyDate: '6.2rem', // Most Recent + sort arrow / 00/00/0000
    DayOfWeekNo: '5.4rem', // Wednesday
};

const getSiteListColumnSx = (property: string, header: string): SxProps<Theme> | undefined => {
    const contentWidth = siteListColumnWidths[property]
        ?? (header.trim().toUpperCase() === 'GFA' ? '3.9rem' : undefined);
    if (!contentWidth) return undefined;

    return {
        ...truncatedColumn(`calc(${contentWidth} + 16px)`),
        // Bound the text itself as well: automatic table layout can expand cells.
        '& > .MuiTypography-root': { maxWidth: contentWidth, mx: 'auto' },
        '& .MuiTableSortLabel-root': {
            whiteSpace: 'pre-line',
            position: 'relative',
            // Keep sorting from increasing the requested content width.
            '& .MuiTableSortLabel-icon': { position: 'absolute', left: '100%', ml: 0, mr: 0, fontSize: 12 },
        },
    };
};

export default function SiteTable({ type, filter, setDeselectionOpen, multiDeselect }: IProps) {
    const isSiteSelectionTable = type === 'summaryFiltering' || type === 'siteFiltering';
    const getColumnSx = type === 'siteList' ? getSiteListColumnSx : getSiteSelectionColumnSx;
    const navigate = useNavigate();
    const location = useLocation();
    const { post, get } = useContext(APIContext);
    const { updateSessionState, sessionState } = useContext(SessionContext);
    const { logSiteListExamined } = useContext(TelemetryContext);
    const [count, setCount] = useState<number>(0);
    const [siteList, setSiteList] = useState<SiteListViewModel[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const sessionHasLoadedRef = useRef<boolean>(false);
    const [mapData, setMapData] = useState<SurveyFullViewModel[] | null>([]);
    const lastTelemetryPayloadRef = useRef<string | null>(null);
    const siteListRequestIdRef = useRef(0);
    const tableKey = type === 'summaryFiltering' ? 'summaryFiltering' : 'siteList';
    const tableState = sessionState.siteListTableState[tableKey];


    useEffect(() => {
        const nextRows = tableState?.rows ?? (type === "summaryFiltering" ? 10 : 20);
        const nextPage = tableState?.page ?? 0;

        updateSessionState({
            path: 'SiteList',
            siteListRows: nextRows,
            siteListPage: nextPage,
        });

        if (type === "summaryFiltering") {
            const forceHide = new Set([
                "MostRecentSurveyDate",
                "DayOfWeekNo",
                "SurveyType",
            ]);
            const next = sessionState.siteListHeaders.map(h =>
                forceHide.has(h.property) ? { ...h, shown: false } : h
            );
            updateSessionState({ siteListHeaders: next }, getData);
        }
        else {
            const forceHide = new Set([
                "MostRecentSurveyDate",
                "DayOfWeekNo",
                "SurveyType",
            ]);
            const next = sessionState.siteListHeaders.map(h =>
                forceHide.has(h.property) ? { ...h, shown: true } : h
            );
            updateSessionState({ siteListHeaders: next }, getData);
        }

        if (filter === undefined) //if defined this method is called in the filter use effect
            getData();
    }, [])

    useEffect(() => { //update the list when toggle selected value has changed and set page number back to 1
        if (filter !== undefined) {
            updateSessionState({
                siteListPage: 0,
                siteListTableState: {
                    ...sessionState.siteListTableState,
                    [tableKey]: { ...tableState, page: 0 }
                }
            }) //update the page up
            getData({ ...sessionState, siteListPage: 0 }); //copy of state as the update session dosn't apply before the API call
        }
    }, [filter])

    useEffect(() => {
        if (sessionHasLoadedRef.current === false && sessionState.selectedSitesAndSurveys.length !== 0) {
            sessionHasLoadedRef.current = true;
            getData();
        }
    }, [sessionState.selectedSitesAndSurveys])

    const getData = (newState?: SessionState, options?: { logTelemetry?: boolean }) => {
        const state = newState ?? sessionState;
        const shouldLogTelemetry = options?.logTelemetry ?? type === 'siteList';
        const requestId = ++siteListRequestIdRef.current;

        //change to only apply when needed?
        const vm = filter ?? new SiteFilterViewModel();

        if (type === 'summaryFiltering') {
            const trFilters = new TripRateCalculationFiltersViewModel()
            trFilters.primaryFilters = state.primaryFilters
            trFilters.secondaryFilters = state.secondaryFilters
            vm.tripRateFilters = trFilters;
            vm.type = type;
        }

        vm.references = type === 'summaryFiltering'
            ? filter?.references ?? state.selectedSitesAndSurveys.map(m => m.surveyRef)
            : filter !== undefined && filter.showOnlySelected === true ?
                sessionState.landusePage.sites :
                null;

        const str = `GetSiteList?orderBy=${state.siteListSort}&order=${state.siteListDir}&page=${state.siteListPage}&pageRows=${type !== 'summaryFiltering' && siteList.length === 0 ? 20 : state.siteListRows}&search=${state.siteListTextSearch}&mainLandUse=${type !== 'siteList' ? state.tripRateMain : state.siteListMain}&subLandUse=${type !== 'siteList' ? state.tripRateSub : state.siteListSub}&landUseSearch=${state.siteListLandSearch}`;
        post(
            "SiteList",
            str,
            vm,
            (data: GetSiteListViewModel) => {
                if (requestId !== siteListRequestIdRef.current) {
                    return;
                }

                setSiteList(data.data);
                setCount(data.count);

                const sourceHeaders = state.siteListHeaders?.length
                    ? state.siteListHeaders
                    : sessionState.siteListHeaders;

                const visibility = new Map(
                    (sourceHeaders ?? []).map(h => [h.property, !!h.shown] as const)
                );

                let mergedHeaders = data.headers.map(h => ({
                    ...h,
                    shown: visibility.has(h.property) ? visibility.get(h.property)! : !!h.shown
                }));

                updateSessionState({ siteListHeaders: mergedHeaders });
                getMapData(state.siteListMain, state.siteListSub);

                if (type === 'siteList' && shouldLogTelemetry) {
                    const payloadKey = JSON.stringify({
                        main: state.siteListMain,
                        sub: state.siteListSub,
                        type: state.siteListType,
                        search: state.siteListTextSearch,
                        landUseSearch: state.siteListLandSearch
                    });

                    if (lastTelemetryPayloadRef.current !== payloadKey) {
                        logSiteListExamined(state);
                        lastTelemetryPayloadRef.current = payloadKey;
                    }
                }
            }
        );
    };

    const getMapData = (main, sub) => {
        get("TripRateFilterNew", `GetSurveyFilterList?landUse=${main}&subuse=${sub}`, setMapData);
    }

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof SiteListViewModel) => {
        const isAsc = sessionState.siteListSort === property && sessionState.siteListDir === 'asc';
        updateSessionState({
            siteListSort: property,
            siteListDir: isAsc ? 'desc' : 'asc'
        }, (newState) => getData(newState, { logTelemetry: false }));
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        updateSessionState({
            siteListPage: newPage,
            siteListTableState: {
                ...sessionState.siteListTableState,
                [tableKey]: { ...tableState, page: newPage }
            }
        }, (newState) => getData(newState, { logTelemetry: false }));
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rows = parseInt(event.target.value);
        updateSessionState({
            siteListPage: 0,
            siteListRows: rows,
            siteListTableState: {
                ...sessionState.siteListTableState,
                [tableKey]: { page: 0, rows }
            }
        }, (newState) => getData(newState, { logTelemetry: false }));
    };

    const setSiteId = (site: SiteListViewModel) => {
        const returnLabel: SiteReturnLabel = type === 'siteList' ? 'Back to Site List' : 'Back to Site Selection';
        navigateToSite(navigate, location, {
            siteRef: site.reference,
            type,
            fromTab: 'siteSummary',
            returnLabel,
            returnTo: type === 'summaryFiltering' ? '/triprate/summary?toTab=siteSummary' : undefined
        });
    }

    const toggleColumn = (property: string) => {
        const newHeaders = sessionState.siteListHeaders.map(cell =>
            cell.property === property ? { ...cell, shown: !cell.shown } : cell
        );
        updateSessionState({ siteListHeaders: newHeaders });
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const lastCells = () => {
        return (
            <>
                {type === 'summaryFiltering' &&
                    <TableCell sx={{ width: 'auto' }}>
                        Reason for deselection
                    </TableCell>
                }
                <TableCell
                    align={type === 'siteFiltering' ? 'right' : 'center'}
                    sx={type === 'summaryFiltering'
                        ? { width: '48px', minWidth: '48px', maxWidth: '48px' }
                        : undefined}
                >
                    <IconButton onClick={handleMenuClick} size="small">
                        {Boolean(anchorEl) ? <CheckCircleIcon color="primary" /> : <AddCircleIcon color="primary" />}
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        slotProps={{
                            paper: {
                                style: {
                                    maxHeight: '55vh',
                                },
                            },
                        }}
                    >
                        {sessionState.siteListHeaders
                            .filter((f, index) => f.shown !== null && index >= 7)
                            .map(cell => (
                                <MenuItem
                                    key={cell.property}
                                    onClick={(e) => e.stopPropagation()}
                                    selected={!!cell.shown}
                                >
                                    <Checkbox
                                        checked={!!cell.shown}
                                        onChange={() => toggleColumn(cell.property)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    {cell.header}
                                </MenuItem>
                            ))}
                    </Menu>
                </TableCell>
            </>
        )
    }

    return (
        <Paper sx={{ height: '100%', pt: '12px', flexDirection: "column", display: "flex" }}>

            <Grid container pb="8px" columnSpacing={1}>
                {type === "siteList" &&
                    <Grid sx={{ flex: '0 0 38rem', maxWidth: '100%' }}>
                        <LanduseSelection area="SiteList" getData={getData} />
                    </Grid>
                }
                <Grid size={type === "siteList" ? 'grow' : 12} sx={{ minWidth: 0 }}>
                    <SiteListTextSearchAndLegend getData={getData} type={type} filter={filter} mapList={mapData ?? undefined} />
                </Grid>
            </Grid >

            <TableContainer>
                <Box sx={{ width: type === 'siteList' ? 'max-content' : '100%', minWidth: type === 'siteList' ? undefined : '100%' }}>
                    <ResponsiveTable
                        stickyHeader
                        sx={{
                            width: type === 'siteList' ? 'max-content' : '100%',
                            tableLayout: 'auto',
                            '& .MuiTableCell-root': {
                                fontSize: '0.8125rem',
                                px: 1,
                            },
                            '& .MuiTypography-root': { fontSize: 'inherit' },
                            '& .MuiTableCell-head': { fontWeight: 700 },
                            '& .MuiTableSortLabel-root': { fontWeight: 700 },
                        }}
                    >
                    <SortableTableHead
                        order={sessionState.siteListDir}
                        orderBy={sessionState.siteListSort}
                        onRequestSort={handleRequestSort}
                        firstCells={isSiteSelectionTable
                            ? <TableCell align="center" sx={{ width: 'calc(7ch + 16px)', minWidth: 'calc(7ch + 16px)', maxWidth: 'calc(7ch + 16px)' }}>Include</TableCell>
                            : <></>}
                        headCells={sessionState.siteListHeaders.filter(f => f.shown).map(m => ({
                            id: m.property,
                            label: type === 'siteList' && m.property === 'MostRecentSurveyDate'
                                ? 'Most Recent\nSurvey'
                                : type === 'siteList' && m.property === 'DayOfWeekNo'
                                    ? 'Day of the\nWeek'
                                    : m.header,
                            property: m.property,
                            disablePadding: true,
                            show: m.shown,
                            sx: getColumnSx(m.property, m.header),
                        } as HeadCell<SiteListViewModel>))}
                        lastCells={lastCells()}
                    />
                    {(sessionState.siteListType !== 'archived' || sessionState.siteListMain !== "") &&
                        <TableBody>
                            {
                                siteList.map((row: SiteListViewModel, index: number) => {
                                    return (<SiteRow key={row.reference + index} row={row} refresh={getData} headCells={sessionState.siteListHeaders.map(m => ({
                                        id: m.property,
                                        label: m.header,
                                        property: m.property,
                                        show: m.shown,
                                        sx: getColumnSx(m.property, m.header),
                                    } as HeadCell<SiteListViewModel>))}
                                        setSiteId={setSiteId}
                                        type={type}
                                        setDeselectionOpen={setDeselectionOpen}
                                        multiDeselect={multiDeselect}
                                    />);
                                })
                            }
                        </TableBody>
                    }
                    </ResponsiveTable>
                    {(sessionState.siteListType !== 'archived' || sessionState.siteListMain !== "") &&
                        <Box sx={{ width: '100%' }}>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 20, 50]}
                                component="div"
                                count={count}
                                rowsPerPage={sessionState.siteListRows}
                                page={sessionState.siteListPage}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                slotProps={{ toolbar: { sx: { height: '30px !important', minHeight: '30px !important', overflow: "hidden" } }, selectLabel: { sx: { margin: '0px' } }, displayedRows: { sx: { margin: '0px' } } }}
                            />
                        </Box>
                    }
                </Box>
            </TableContainer>
        </Paper>
    );
}
